import type { ReactNode } from "react";
import { Link } from "react-router";
import { motion } from "framer-motion";
import { ArrowRight, ArrowUpRight, Lock, Play } from "lucide-react";
import { upcomingLives } from "@/lib/schedule";
import { FOLLOW, Signup, useVideoCards } from "../../ConceptBits";
import { CLUB, EASE, HEART, HOME, PAGES, datePart, useFonts } from "../bits";
import { EventStrip } from "../EventStrip";
import { EVENT, EVENT_ENDS } from "../events/event";
import { Part, rootStyle } from "../parts";
import "../../HomeRound5.css";
import "../base.css";
import "./Links.css";

/* Link-in-bio for Instagram and TikTok: a compact blue header with Chelsea,
   then thumb-sized link cards, a newsletter signup, and socials. Phone first;
   a centered column on desktop. */

type Item = { key: string; el: ReactNode };

/** One link card: internal routes use Link, everything else opens in a new tab. */
function Card({ to, className, children, label }: { to: string; className: string; children: ReactNode; label?: string }) {
  const inner = <>{children}<span className="bl-go" aria-hidden="true">{to.startsWith("/") ? <ArrowRight size={18} /> : <ArrowUpRight size={18} />}</span></>;
  return to.startsWith("/")
    ? <Link to={to} className={`bl-card ${className}`} aria-label={label}>{inner}</Link>
    : <a href={to} target="_blank" rel="noreferrer" className={`bl-card ${className}`} aria-label={label}>{inner}</a>;
}

export function Blue2Links() {
  useFonts();
  const [next] = upcomingLives(1);
  const [video] = useVideoCards(1);
  const club = next?.kind === "club";
  const eventOn = Date.now() < EVENT_ENDS.getTime();

  const items: Item[] = [
    ...(next ? [{ key: "live", el: <Card to={next.url} className="bl-live" label={`Next live: ${datePart(next.start, { weekday: "long", month: "long", day: "numeric" })}, 7 PM Mountain, ${club ? "club class on Patreon" : "free on YouTube"}`}>
      <span className={`bl-cal bl-cal-${next.kind}`} aria-hidden="true"><small>{datePart(next.start, { weekday: "short" })}</small><b>{datePart(next.start, { day: "numeric" })}</b></span>
      <span className="bl-text">
        <span className="bl-kicker">{club ? <><Lock size={11} aria-hidden="true" /> Next club class</> : <><Play size={11} fill="currentColor" aria-hidden="true" /> Next live · free</>}</span>
        <b>{club ? "Sew with me live" : "Live on YouTube"}</b>
        <span className="bl-sub">{datePart(next.start, { month: "short", day: "numeric" })} · 7 PM MT · {club ? "members on Patreon" : "everyone welcome"}</span>
      </span>
    </Card> }] : []),
    ...(eventOn ? [{ key: "event", el: <Card to={EVENT.href} className="bl-event">
      <img className="bl-poster" src={EVENT.poster} alt="" width={1080} height={1350} />
      <span className="bl-text">
        <span className="bl-kicker">{EVENT.kicker}</span>
        <b>{EVENT.headline[0]} <i>{EVENT.film}</i> {EVENT.headline[1]}</b>
        <span className="bl-sub">{EVENT.title} · {EVENT.weekday}, {EVENT.month} {EVENT.day} · {EVENT.time}</span>
      </span>
    </Card> }] : []),
    { key: "club", el: <Card to={CLUB} className="bl-club">
      <span className="bl-text">
        <span className="bl-kicker">Loose Threads Club</span>
        <b>Come sew with me</b>
        <span className="bl-sub">$25/month · live classes + every replay</span>
      </span>
      <span className="bl-hand" aria-hidden="true">you in? ♡</span>
    </Card> },
    ...(video ? [{ key: "video", el: <Card to={video.url} className="bl-video">
      <span className="bl-thumb"><img src={video.img} alt="" style={{ objectPosition: video.pos }} /><i aria-hidden="true"><Play size={14} fill="currentColor" /></i></span>
      <span className="bl-text">
        <span className="bl-kicker">Latest video</span>
        <b className="bl-clamp">{video.title}</b>
      </span>
    </Card> }] : []),
    { key: "faves", el: <Card to={PAGES.favorites} className="bl-faves">
      <span className="bl-text"><b>My favorites</b><span className="bl-sub">Tools, machines, and fabric I actually use</span></span>
    </Card> },
    { key: "retreats", el: <Card to={PAGES.retreats} className="bl-retreats">
      <span className="bl-text"><b>Sewing retreats</b><span className="bl-sub">A few days away to sew, eat, and laugh with me</span></span>
    </Card> },
    { key: "about", el: <Card to={PAGES.about} className="bl-about">
      <span className="bl-text"><b>About me</b><span className="bl-sub">Hi, it’s me! Get to know me</span></span>
    </Card> },
  ];

  return <div className="h5 b2 bl" style={rootStyle}>
    <EventStrip />
    <Part p="hero">
      <header className="bl-head">
        <Link to={HOME} className="b2-logo bl-logo">A Few Loose Threads</Link>
        <motion.span className="bl-avatar" initial={{ opacity: 0, scale: 0.85, rotate: -6 }} animate={{ opacity: 1, scale: 1, rotate: -3 }} transition={{ duration: 0.6, ease: EASE }}>
          <img src="/chelsea-cutout-trim.webp" alt="Chelsea smiling in a straw hat" width={417} height={1141} />
        </motion.span>
        <h1 className="bl-title" aria-label="Hi, I’m Chelsea">
          <span className="bl-hi" aria-hidden="true">Hi, I’m</span>
          <span className="bl-name" aria-hidden="true">chelsea</span>
        </h1>
        <p className="bl-intro">I make colorful things (and I’d love for you to make them too).</p>
      </header>
    </Part>

    <Part p="club">
      <main className="bl-sheet">
        <ul className="bl-list">
          {items.map((it, i) => <motion.li key={it.key} initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 + i * 0.06, duration: 0.45, ease: EASE }}>{it.el}</motion.li>)}
        </ul>

        <section className="bl-loop" aria-labelledby="bl-loop-title">
          <h2 id="bl-loop-title" className="bl-loop-title">Stay in the loop <svg viewBox="0 0 30 46" aria-hidden="true"><path d={HEART} /></svg></h2>
          <p className="bl-loop-sub">One email a week. Mostly fabric.</p>
          <Signup source="links-blue-2" className="bl-signup" button={<>Sign up <ArrowRight size={16} aria-hidden="true" /></>} done={<>You’re in! Watch your inbox <span aria-hidden="true">♡</span></>} />
        </section>

        <ul className="bl-social" aria-label="Follow along">
          {FOLLOW.map(f => <li key={f.s.key}><a href={f.s.url} target="_blank" rel="noreferrer" aria-label={`${f.s.platform} ${f.s.handle}`}>{f.icon}</a></li>)}
        </ul>
        <p className="bl-fine"><Link to={HOME}>afewloosethreads.com</Link></p>
      </main>
    </Part>
  </div>;
}
